import {
  NormalizedReport,
  ReportChip,
  ReportDetail,
  ReportFailureReason,
  ReportGroup,
  ReportRule,
  ReportSourceFile,
  ReportStructure,
  resolveFailureReasonLabel
} from './normalized-report.model';
import {findVerdictInFields, parseVerdictValue} from './report-display-config';
import {averageRatedPercent, roundPercentPreservingExtremes, worstStatus} from './report-leaves';

/**
 * Adapter-Interface: Transformiert rohe Check-Ergebnisse in das einheitliche NormalizedReport-Format.
 */
export interface CheckReportAdapter {
  parseReportData(raw: any): NormalizedReport;
}

/**
 * Normalisiert ein IDS-Ergebnis (0–1 oder bereits 0–100) auf eine ganzzahlige Prozentangabe.
 *
 * Rundet über {@link roundPercentPreservingExtremes}: ein Rohwert unter 1.0 darf nie als 100 %
 * erscheinen. Genau daran lag der widersprüchliche Bericht – ein Dateiscore von 0.9987 wurde zu
 * „100 %", während die Spezifikation darunter ihre echten 98 % zeigte.
 */
function toPercent(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  const pct = value <= 1 ? value * 100 : value;
  return roundPercentPreservingExtremes(pct);
}

/**
 * Prüfer*in-Name aus dem Rohbericht: erster NICHT-LEERER Wert aus camelCase- oder snake_case-Feld.
 * Wichtig: Nullish-Coalescing (`??`) reicht hier nicht, da ein leerer String ('') – z. B. ein
 * leeres `checkerName` im outputData der KI-Prüfung – einen echten Wert (`checker_name`) sonst
 * verdecken würde.
 */
function pickCheckerName(raw: any): string {
  for (const candidate of [raw?.checkerName, raw?.checker_name]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }
  return '';
}

/** Status-Ableitung aus einem Prozentwert. */
function statusFromPercent(percent: number): 'success' | 'warning' | 'error' {
  if (percent >= 100) return 'success';
  if (percent >= 50) return 'warning';
  return 'error';
}

/** Wandelt einen rohen IDS-Wert (string/zahl/objekt) in einen Anzeigetext. */
function valueToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map(valueToText).filter(Boolean).join(', ');
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

function isBimBaseSpecification(name: unknown): boolean {
  if (typeof name !== 'string') return false;
  const normalizedName = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalizedName.includes('bim base check')
    || normalizedName.includes('bim basis check')
    || normalizedName.includes('base check')
    || normalizedName.includes('basis check');
}

function parseStringToArray(value: string): string[] | null {
  if (!value) return null;
  const trimmed = value.trim();
  const jsonNormalized = trimmed.replace(/'/g, '"');

  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(jsonNormalized);
      if (Array.isArray(parsed)) {
        const values = parsed.map(entry => String(entry).trim()).filter(Boolean);
        return values.length > 0 ? values : null;
      }
    } catch {
      const inner = trimmed.slice(1, -1);
      const parts = inner
        .split(',')
        .map(entry => entry.trim().replace(/^['"]|['"]$/g, '').trim())
        .filter(Boolean);
      return parts.length > 0 ? parts : null;
    }
  }

  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(jsonNormalized);
      if (typeof parsed === 'object' && parsed !== null) {
        return formatConstraintObject(parsed as Record<string, unknown>);
      }
    } catch {
      const inner = trimmed.slice(1, -1);
      const parts = inner
        .split(',')
        .map(entry => {
          const [key, val] = entry.split(':').map(part => part.trim().replace(/^['"]|['"]$/g, '').trim());
          return key && val ? {key, val} : null;
        })
        .filter((entry): entry is { key: string; val: string } => entry !== null);
      if (parts.length > 0) {
        return formatConstraintObject(Object.fromEntries(parts.map(part => [part.key, part.val])));
      }
    }
  }

  return null;
}

function formatConstraintObject(obj: Record<string, unknown>): string[] {
  const keys = Object.keys(obj);
  const minIncl = obj['minInclusive'];
  const maxIncl = obj['maxInclusive'];
  const minExcl = obj['minExclusive'];
  const maxExcl = obj['maxExclusive'];

  const hasMin = minIncl != null || minExcl != null;
  const hasMax = maxIncl != null || maxExcl != null;

  if (hasMin && hasMax && keys.length === 2) {
    const minVal = minIncl ?? minExcl;
    const maxVal = maxIncl ?? maxExcl;
    const minBracket = minIncl != null ? '[' : '(';
    const maxBracket = maxIncl != null ? ']' : ')';
    return [`${minBracket}${minVal} - ${maxVal}${maxBracket}`];
  }

  const constraintLabels: Record<string, string> = {
    minInclusive: '>=',
    maxInclusive: '<=',
    minExclusive: '>',
    maxExclusive: '<',
    pattern: 'Muster',
    enumeration: 'Enum',
    length: 'Laenge',
    minLength: 'Min. Laenge',
    maxLength: 'Max. Laenge',
  };

  return keys.map(key => `${constraintLabels[key] ?? key} ${String(obj[key])}`);
}

function normalizeDisplayValue(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) {
    return value.flatMap(entry => normalizeDisplayValue(entry));
  }

  const text = valueToText(value).trim();
  if (!text) return [];

  const parsed = parseStringToArray(text);
  if (parsed && parsed.length > 0) {
    return parsed;
  }
  return [text];
}

function flattenDisplayValues(values: unknown, singleValue?: unknown): string[] {
  if (Array.isArray(values) && values.length > 0) {
    return values.flatMap(entry => normalizeDisplayValue(entry));
  }
  return normalizeDisplayValue(singleValue);
}

function cleanDisplayValue(value: unknown): string {
  const parsed = normalizeDisplayValue(value);
  return parsed.length > 0 ? parsed.join(', ') : valueToText(value).trim();
}

function statusFromIdsBooleanOrPercent(status: unknown, percent: number): 'success' | 'warning' | 'error' {
  if (typeof status === 'boolean') return status ? 'success' : 'error';
  return statusFromPercent(percent);
}

/**
 * Gründe, die eine „weiche" Abweichung beschreiben: die geforderte Angabe ist am Bauteil
 * vorhanden, entspricht aber nicht exakt der Vorgabe.
 *
 * Nur der Datentyp zählt hier hinein. Ein abweichender **Wert** ist bewusst kein weicher Fall –
 * er ist inhaltlich falsch, nicht bloß anders typisiert.
 */
const SOFT_FAILURE_REASON_TYPES = new Set(['DATATYPE']);

/**
 * Liest `failureReasons` aus der Modul-Ausgabe; fehlt das Feld, entsteht kein leeres Array.
 *
 * @param facetLabel Facettentyp der Anforderung, siehe {@link resolveFailureReasonLabel}.
 */
function mapFailureReasons(raw: unknown, facetLabel: string): ReportFailureReason[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const reasons: ReportFailureReason[] = [];
  for (const entry of raw) {
    const type = typeof entry?.type === 'string' ? entry.type : '';
    if (!type) continue;
    const actualValues = Array.isArray(entry?.actualValues)
      ? entry.actualValues.map((value: unknown) => String(value)).filter(Boolean)
      : undefined;
    const fallbackLabel = typeof entry?.label === 'string' && entry.label ? entry.label : type;
    reasons.push({
      type,
      label: resolveFailureReasonLabel(facetLabel, type, fallbackLabel),
      count: Number(entry?.count ?? 0),
      actualValues: actualValues && actualValues.length > 0 ? actualValues : undefined,
      expected: typeof entry?.expected === 'string' && entry.expected ? entry.expected : undefined,
    });
  }
  return reasons.length > 0 ? reasons : undefined;
}

/**
 * True, wenn ALLE Fehlschläge einer Anforderung weiche Abweichungen sind.
 *
 * „Alle" ist der Punkt: fehlt bei einem Teil der Bauteile das PropertySet ganz, bleibt die
 * Anforderung ein Fehler – die Warnung würde den härteren Befund sonst verdecken.
 */
function softFailureOnly(reasons: ReportFailureReason[] | undefined): boolean {
  if (!reasons || reasons.length === 0) return false;
  return reasons.every(reason => SOFT_FAILURE_REASON_TYPES.has(reason.type));
}

/** Deutsches Label der IDS-Kardinalität (required/optional/prohibited) einer Anforderung. */
export function cardinalityFallback(cardinality: unknown): string | null {
  if (typeof cardinality !== 'string') return null;
  const normalized = cardinality.trim().toLowerCase();
  if (normalized === 'required') return 'Erforderlich';
  if (normalized === 'optional') return 'Optional';
  if (normalized === 'prohibited') return 'Verboten';
  return null;
}

/** Baut die Anwendbarkeits-Chips einer Spezifikation aus den IDS-Applicabilities. */
function buildApplicabilityChips(applicabilities: any[]): ReportChip[] {
  return (applicabilities ?? []).map(app => {
    const label = (app?.facetTypeLabel ?? app?.facetType ?? '').toString().trim() || undefined;
    const name = cleanDisplayValue(app?.name) || undefined;
    const values = flattenDisplayValues(app?.values, app?.value);
    return {
      label,
      name,
      value: values[0] ?? '',
      rest: values.slice(1),
    };
  }).filter(chip => chip.value || chip.label || chip.name);
}

// ─── IDS Adapter ────────────────────────────────────────────────────────────────

export class IdsReportAdapter implements CheckReportAdapter {
  parseReportData(raw: any): NormalizedReport {
    const groups = this.buildIdsGroups(raw);
    const fallbackScore = averageRatedPercent(groups.map(group => group.percent));
    // `score === null` ist ein echter Wert des Moduls ("nicht anwendbar": keine Spezifikation
    // traf auf ein Bauteil zu) und darf NICHT über den Fallback zu einer Zahl werden – sonst
    // behauptet der Bericht ein Ergebnis, das nie geprüft wurde.
    const score = raw?.score === null
      ? null
      : typeof raw?.score === 'number' ? toPercent(raw.score) : fallbackScore;
    const fallbackFileName = raw?.file_name ?? raw?.fileName ?? 'Geprüfte Datei';
    const effectiveGroups = groups.length > 0
      ? groups
      : [{
        name: fallbackFileName,
        percent: score,
        status: score == null ? 'unknown' as const : statusFromPercent(score),
        rules: [],
      }];
    const ruleCount = effectiveGroups.reduce((sum, group) => sum + group.rules.length, 0);

    return {
      meta: {
        checkerName: pickCheckerName(raw),
        checkDate: raw.check_date ? new Date(raw.check_date) : new Date(),
        rulesetName: raw.rule_name ?? raw.ruleName ?? raw.ruleset_name ?? raw.rulesetName ?? 'IDS-Check',
        fileName: effectiveGroups[0]?.name ?? fallbackFileName,
        fileCount: effectiveGroups.length,
        ruleCount,
        score,
        resultInfo: typeof raw?.resultInfo === 'string' && raw.resultInfo.trim()
          ? raw.resultInfo.trim()
          : undefined,
      },
      groups: effectiveGroups,
    };
  }

  private buildIdsGroups(raw: any): ReportGroup[] {
    const groupInputs: any[] = [];

    if (Array.isArray(raw?.results)) {
      for (const resultEntry of raw.results) {
        const candidate = resultEntry?.outputs && typeof resultEntry.outputs === 'object'
          ? resultEntry.outputs
          : resultEntry;
        if (candidate && typeof candidate === 'object' && Array.isArray(candidate.specifications)) {
          groupInputs.push(candidate);
        }
      }
    }

    if (Array.isArray(raw?.files)) {
      for (const fileEntry of raw.files) {
        if (fileEntry && typeof fileEntry === 'object' && Array.isArray(fileEntry.specifications)) {
          groupInputs.push(fileEntry);
        }
      }
    }

    if (Array.isArray(raw?.specifications)) {
      groupInputs.push(raw);
    }

    const groupedByName = new Map<string, ReportGroup>();
    groupInputs.forEach((groupInput, index) => {
      const rules = this.mapIdsRules(groupInput.specifications ?? []);
      // Nur als Fallback, wenn die Datei keine Spezifikationen mitbringt: der Modul-Score
      // mittelt einen anderen Nenner (nur anwendbare Specs) als die unten angezeigte Liste
      // (Specs mit 0 Bauteilen werden gefiltert) und wies dadurch 100 % über einer 98-%-Regel
      // aus. Der angezeigte Elternwert muss der Mittelwert seiner angezeigten Kinder sein.
      const inputPercent = typeof groupInput?.score === 'number'
        ? toPercent(groupInput.score)
        : null;
      const groupName = groupInput?.file_name
        ?? groupInput?.fileName
        ?? groupInput?.name
        ?? `Datei ${index + 1}`;
      const key = String(groupName).trim() || `Datei ${index + 1}`;
      const existing = groupedByName.get(key);
      const combinedRules = existing ? [...existing.rules, ...rules] : rules;
      const ruleAveragePercent = averageRatedPercent(combinedRules.map(rule => rule.percent));
      const groupPercent = ruleAveragePercent ?? inputPercent;
      groupedByName.set(key, {
        name: groupName,
        percent: groupPercent,
        status: groupPercent == null ? 'unknown' : statusFromPercent(groupPercent),
        rules: combinedRules,
      });
    });

    return Array.from(groupedByName.values());
  }

  private mapIdsRules(rawSpecifications: any[]): ReportRule[] {
    const hasAnyPositiveElements = rawSpecifications.some(spec => Number(spec?.totalElements ?? 0) > 0);
    const specifications = hasAnyPositiveElements
      ? rawSpecifications.filter(spec =>
        Number(spec?.totalElements ?? 0) > 0 || isBimBaseSpecification(spec?.name),
      )
      // If all specs report 0 elements (valid IDS outcome), keep all to avoid an empty report.
      : rawSpecifications;

    return specifications.map(spec => {
      const specPercent = toPercent(spec.result);
      const requirements: any[] = spec.requirements ?? [];

      const details: ReportDetail[] = requirements.map(req => {
        const reqPercent = toPercent(req.result);
        const values = flattenDisplayValues(req.values, req.value);
        const cardinalityValue = cardinalityFallback(req.cardinality);
        const effectiveValues = values.length > 0
          ? values
          : cardinalityValue
            ? [cardinalityValue]
            : [];
        const valueChips: ReportChip[] = values.length > 0
          ? [{value: effectiveValues[0], rest: effectiveValues.slice(1)}]
          : effectiveValues.length > 0
            ? [{value: effectiveValues[0], rest: effectiveValues.slice(1)}]
            : [];
        const facetLabel = (req.facetTypeLabel ?? req.facetType ?? '').toString().trim();
        const name = cleanDisplayValue(req.name);

        const failureReasons = mapFailureReasons(req.failureReasons, facetLabel);

        return {
          // Scheitert die Anforderung AUSSCHLIESSLICH an einer weichen Abweichung (der Datentyp
          // stimmt nicht, die Eigenschaft ist aber da), wird sie als Warnung statt als Fehler
          // dargestellt. Bewusst nur die Darstellung: `percent` und der Score des Prüfmoduls
          // bleiben unberührt, sonst würden sich bestehende Berichte rückwirkend anders rechnen.
          type: softFailureOnly(failureReasons)
            ? 'warning'
            : statusFromIdsBooleanOrPercent(req.status, reqPercent),
          text: [facetLabel, name].filter(Boolean).join(': '),
          count: (req.guidListCorrect?.length ?? 0) + (req.guidListFail?.length ?? 0),
          facetLabel: facetLabel || undefined,
          name: name || undefined,
          valueChips,
          percent: reqPercent,
          rawOutput: req,
          failureReasons,
        };
      });

      return {
        name: spec.name ?? 'Spezifikation',
        percent: specPercent,
        status: statusFromIdsBooleanOrPercent(spec.status, specPercent),
        applicabilities: buildApplicabilityChips(spec.applicabilities),
        elementCount: typeof spec.totalElements === 'number' ? spec.totalElements : 0,
        notApplicable: spec.notApplicable === true,
        details,
      };
    });
  }
}

// ─── Spezifikations-basierte Modul-Checks (BIM-Basis-Check, Qualitätscheck) ─────

/**
 * Selbstbeschreibende Prüftypen, deren Modul-Output Regelergebnisse als `specifications`
 * mitbringt (`CHECK_TYPE` in `bim_basis_checker.py`).
 *
 * Ein Set statt einer Konstante, weil der Zweig nicht an ein Modul gebunden ist: jedes Modul mit
 * demselben Spezifikations-Schema kann hier andocken.
 *
 * Der All-Right Check stand hier und ist wieder heraus: seit ein Knoten genau EINE Prüfregel
 * prüft, endet seine Regel in einer Nutzerantwort — und im Bericht landen deren Ausgaben, nicht
 * die des Prüfschritts (`WorkflowEngine.extractElementVerdict`). Seine Befunde stehen im
 * Antworttext.
 */
const SPEC_BASED_CHECK_TYPES = new Set(['BIM_BASIS']);

/** Eine vom BIM-Basis-Check bzw. Qualitätscheck geprüfte Datei mit ihren Regelergebnissen. */
interface SpecBasedFileEntry {
  fileName?: string;
  score?: number;
  totalCheckedElements?: number;
  specifications: any[];
}

function finiteNumberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function nonEmptyStringOrUndefined(value: unknown): string | undefined {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || undefined;
}

/**
 * Datei-Einträge eines spezifikations-basierten Modul-Outputs – oder `null`, wenn der Output keiner ist.
 *
 * Erkennung über den selbstbeschreibenden `checkType`, nicht über den Regelnamen: die
 * Namensheuristik `isBimBaseSpecification` oben gehört zum Legacy-Pfad (BIM-Tool `POST /base-check`),
 * hier gibt das Modul selbst Auskunft. Ein fehlgeschlagener Lauf trägt bewusst KEIN `checkType`
 * (`handler.py` setzt es nur im Erfolgszweig) und läuft damit weiter über die generische
 * Verdikt-Darstellung, die seine Fehlermeldung zeigt.
 *
 * Zwei Formen, beide aus `handler.py`: eine Datei trägt `specifications` direkt, ein Mehrdatei-Lauf
 * legt sie je Eintrag in `results` ab (`_execute_multi_file`).
 */
function specBasedFileEntries(outputs: any): SpecBasedFileEntry[] | null {
  if (!outputs || typeof outputs !== 'object') return null;
  if (!SPEC_BASED_CHECK_TYPES.has(String(outputs.checkType ?? '').toUpperCase())) return null;

  if (Array.isArray(outputs.specifications)) {
    return [{
      fileName: nonEmptyStringOrUndefined(outputs.file_name ?? outputs.fileName),
      score: finiteNumberOrUndefined(outputs.score),
      totalCheckedElements: finiteNumberOrUndefined(outputs.totalCheckedElements),
      specifications: outputs.specifications,
    }];
  }

  if (Array.isArray(outputs.results)) {
    const entries: SpecBasedFileEntry[] = outputs.results
      .filter((entry: any) => entry && typeof entry === 'object' && Array.isArray(entry.specifications))
      .map((entry: any) => ({
        fileName: nonEmptyStringOrUndefined(entry.file_name ?? entry.fileName),
        score: finiteNumberOrUndefined(entry.score),
        totalCheckedElements: finiteNumberOrUndefined(entry.totalCheckedElements),
        specifications: entry.specifications,
      }));
    return entries.length > 0 ? entries : null;
  }

  return null;
}

/**
 * Prüfergebnis einer BIM-Basis- bzw. Qualitätsregel.
 *
 * `severity` hat Vorrang vor dem booleschen `status`: die Regeln melden dreiwertig, und ein
 * Boolean-Vergleich würde „warning" (teilweise erfüllt) auf „nicht erfüllt" kollabieren lassen –
 * genau dafür führen die Module `severity` mit (`_map_rule_to_specification`).
 *
 * Eine nicht anwendbare Regel (kein passendes Bauteil im Modell) ist `unknown`, nicht `error`: sie
 * geht modulseitig auch nicht in den Score ein.
 */
function specBasedSpecStatus(spec: any, percent: number): 'success' | 'warning' | 'error' | 'unknown' {
  if (spec?.notApplicable === true) return 'unknown';
  switch (String(spec?.severity ?? '').toLowerCase()) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return statusFromIdsBooleanOrPercent(spec?.status, percent);
  }
}

/** Eine Regel (Spezifikation) → Facetten-Zeile der Ergebnis-Darstellung. */
function specBasedSpecDetail(spec: any): ReportDetail {
  const requirement = Array.isArray(spec?.requirements) ? spec.requirements[0] : undefined;
  const facetLabel = (requirement?.facetTypeLabel ?? requirement?.facetType ?? '').toString().trim();
  const name = cleanDisplayValue(spec?.name);
  const percent = toPercent(spec?.result);
  // Beschreibung der Regel; bei einer technisch fehlgeschlagenen Regel steht die Ursache in `text`
  // (`_error_specification`) und ist die einzige Erklärung, die der Bericht anbieten kann.
  const chipValue = cleanDisplayValue(requirement?.name) || cleanDisplayValue(spec?.text);
  const count = finiteNumberOrUndefined(spec?.totalElements)
    ?? (requirement?.guidListCorrect?.length ?? 0) + (requirement?.guidListFail?.length ?? 0);

  return {
    type: specBasedSpecStatus(spec, percent),
    text: [facetLabel, name].filter(Boolean).join(': '),
    count,
    facetLabel: facetLabel || undefined,
    name: name || undefined,
    valueChips: chipValue ? [{value: chipValue}] : [],
    // Nicht anwendbar heißt „nicht bewertet" – eine 0 % behauptete ein Ergebnis, das es nicht gibt.
    percent: spec?.notApplicable === true ? undefined : percent,
    // Für "Öffnen im Viewer" (liest guidListFail/guidListCorrect generisch über rawOutput).
    rawOutput: requirement ?? spec,
  };
}

// ─── Hybrid Adapter ─────────────────────────────────────────────────────────────

export class HybridReportAdapter implements CheckReportAdapter {
  /** Verdikt-Schlüssel, die NICHT als eigene Ergebnis-Felder dargestellt werden (intern/aggregiert/technisch). */
  private static readonly INTERNAL_VERDICT_KEYS = new Set([
    // Aggregat/Verdikt-intern
    'erfuellt', 'status', 'success', 'rule_name', 'element_count', 'results', 'antworten',
    'antwort', 'score', 'erfuellt_count', 'nicht_erfuellt_count', 'unbewertet_count',
    // Technische Ausführungs-/Log-Felder (nie im Bericht darstellen)
    'logs', 'context', 'correlationId', 'correlation_id', 'timestamp', 'timestamps',
    'moduleId', 'module_id', 'durationMs', 'duration_ms', 'phase', 'message', 'level',
    'version', 'aiUsage', 'ai_usage', 'variablen', 'error', 'errorInfo', 'details',
    // Technische KI-Ausführungsfelder. Kanonisch steht das alles in `aiUsage` (bereits oben
    // geblockt); `tokens_verbraucht`/`modell_version` sind der ältere, flache Duplikat-Pfad und
    // werden modulseitig entfernt. Die Liste hier ist die Kompatibilitätsschicht für ALTLÄUFE:
    // deren gespeichertes results-JSON trägt die Felder weiterhin, und der Bericht wird bei jedem
    // Öffnen neu geparst. Ohne sie ist `fields` nie leer, der Fallback `defaultVerdictText()`
    // greift nicht, und ein Verzweigungs-Zweig ohne Nutzerantwort zeigt statt der Erklärung, warum
    // kein Ergebnis vorliegt, einen Salat aus Tokenzahl und Modellnamen.
    'tokens_verbraucht', 'modell_version', 'konfidenz', 'pruefergebnis',
    'teilweise_erfuellt_count', 'unbewertet', 'pruefebene', 'parameter_definitionen', 'extrahiert',
    // Begründung eines unbewerteten Ergebnisses (Backend: WorkflowEngine#unratedVerdict). Sie ist
    // die Quelle des Erklärtexts in `defaultVerdictText` und darf nicht zusätzlich als rohes
    // Ergebnis-Feld erscheinen. `verzweigung_info` ist derselbe Satz an der Quelle: das
    // verzweigende Modul liefert ihn in JEDEM Lauf mit (shared/branch_info.py), also auch dann,
    // wenn ein Verzweigungs-Knoten selbst der letzte Schritt der Regel ist.
    'unbewertet_grund', 'unbewertet_schritt', 'unbewertet_meldung', 'verzweigung_info',
    // Aggregat-Zähler des Backends (putErfuelltVerdict / markHybridStepCompleted): Statistik,
    // kein Ergebnis-Feld.
    'unbekannt_count', 'scored_count', 'abgebrochen',
    // Extraktions-Metadaten des Trigger-Knotens (eingangsparameter). Sie können als Verdikt
    // erscheinen, wenn eine Regel keine Prüfschritte hat und ihr Trigger-Output das Ergebnis wird –
    // deshalb hier geblockt, wie die älteren Geschwister 'extrahiert'/'parameter_definitionen'.
    'identifikator_slug', 'dokument_zeichen', 'dokument_fragmente', 'dokument_gekuerzt',
    'extraktion_fenster',
  ]);

  parseReportData(raw: any): NormalizedReport {
    const ruleEntries: any[] = Array.isArray(raw.results) ? raw.results : [];
    const rulesetName = raw.ruleset_name ?? raw.rulesetName ?? 'Hybrid-Prüfung';
    const topFileName = raw.file_name ?? raw.fileName ?? rulesetName;
    const topFileFormat = (raw.file_type ?? raw.fileType ?? '').toString().trim().toUpperCase() || undefined;
    const identSlug: string | undefined = raw.identifikator_slug ?? raw.identifikatorSlug ?? undefined;

    // Eine Gruppe je geprüfter Datei. Die Regeln laufen pro Datei einmal (Fan-Out im Backend),
    // jeder Ergebnis-Eintrag trägt seinen Dateinamen. Reihenfolge des ersten Auftretens erhalten.
    type FileAccumulator = {
      fileName: string;
      structuresByPosition: Map<string, ReportStructure>;
      flatRules: ReportRule[];
    };
    const fileGroups = new Map<string, FileAccumulator>();
    const fileOrder: string[] = [];
    const ensureGroup = (name: string): FileAccumulator => {
      const key = (name || '').trim() || topFileName;
      let group = fileGroups.get(key);
      if (!group) {
        group = {fileName: key, structuresByPosition: new Map(), flatRules: []};
        fileGroups.set(key, group);
        fileOrder.push(key);
      }
      return group;
    };

    ruleEntries.forEach((ruleEntry, ruleIdx) => {
      const ruleOutputs = ruleEntry?.outputs ?? {};
      const ruleName = ruleEntry?.name ?? ruleEntry?.rule_name ?? ruleOutputs.rule_name ?? `Prüfregel ${ruleIdx + 1}`;
      const ruleShortCode = (ruleEntry?.shortCode ?? ruleEntry?.short_code
        ?? ruleOutputs.shortCode ?? ruleOutputs.__shortCode ?? '').toString().trim() || undefined;
      const entryFileName = (ruleEntry?.file_name ?? ruleEntry?.fileName
        ?? ruleOutputs.file_name ?? ruleOutputs.fileName ?? topFileName ?? '').toString();
      const group = ensureGroup(entryFileName);

      // BIM-Basis-Check und Qualitätscheck: die Regelergebnisse stehen als `specifications` im
      // Modul-Output und wären als Verdikt-Feld verloren – `verdictFields` verwirft jeden Array-/Objektwert. Sie werden
      // hier zu Facetten-Zeilen, und der Prozentwert der Regel kommt aus dem Modul-Score statt aus
      // dem 0/50/100 des Verdikts: „nicht erfüllt" hätte sonst 0 % gezeigt und damit behauptet,
      // keine einzige der Regeln sei erfüllt.
      //
      // MUSS vor `hasRealElements` stehen: ein Mehrdatei-Lauf trägt `results` (ein Eintrag je
      // Datei), was der generische Zweig unten als Element-Fan-out läse und zu „Position 1" machte.
      const specBasedEntries = specBasedFileEntries(ruleOutputs);
      if (specBasedEntries) {
        specBasedEntries.forEach(entry => {
          const details = entry.specifications.map(specBasedSpecDetail);
          const statuses = details.map(detail => detail.type);
          const percent = entry.score != null ? toPercent(entry.score) : this.avgStatusPercent(statuses);
          // Prozent und Status müssen dieselbe Aussage transportieren, sonst zeigt die Regel z. B.
          // 100 %, Badge, Ergebnisdiagramm, Excel und PDF führen sie aber als „unbekannt" oder
          // „nicht erfüllt". Das gilt
          // auch bei mehreren Dateien: `entry.score` ist der Score genau dieser Datei, nicht das
          // Gesamtverdikt über alle Dateien – eine saubere Datei bleibt also grün, auch wenn eine
          // andere durchfällt. Nur ohne jeden bewerteten Wert entscheidet das schlechteste Einzelergebnis.
          const status = percent != null
            ? statusFromPercent(percent)
            : this.worstStatus(statuses);
          ensureGroup(entry.fileName ?? entryFileName).flatRules.push({
            name: ruleName,
            percent,
            status,
            details,
            shortCode: ruleShortCode,
            elementCount: entry.totalCheckedElements,
          });
        });
        return;
      }

      // Element-basierte Regel → results[]; einzelne (nicht element-basierte) Regel → Verdikt direkt.
      // Nur bei echtem Fan-Out (results[]) existiert eine Strukturebene; im Gesamtdokument-Fall
      // (kein results[]) ist der Fallback rein technisch und darf keine Struktur-Position erzeugen.
      const hasRealElements = Array.isArray(ruleOutputs.results) && ruleOutputs.results.length > 0;
      const elements: any[] = hasRealElements
        ? ruleOutputs.results
        : [{outputs: ruleOutputs, element: {}}];

      const ruleDetails: ReportDetail[] = [];
      const ruleStatuses: Array<'success' | 'warning' | 'error' | 'unknown'> = [];

      elements.forEach((element, elementIdx) => {
        const verdict = (element?.outputs && typeof element.outputs === 'object') ? element.outputs : {};
        const elementData = (element?.element && typeof element.element === 'object') ? element.element : {};
        const type = this.verdictStatus(verdict);
        ruleStatuses.push(type);

        const fields = this.verdictFields(verdict);
        const answer = typeof verdict.antwort === 'string' ? verdict.antwort.trim() : '';
        const detail: ReportDetail = {
          type,
          // Antwort der Nutzerantwort; sonst (kein Terminal-Antworttext) ein Status-Fallback (E6).
          text: answer || (Object.keys(fields).length > 0 ? '' : this.defaultVerdictText(type, verdict)),
          count: 0,
          fields: Object.keys(fields).length > 0 ? fields : undefined,
        };
        ruleDetails.push(detail);

        if (hasRealElements) {
          const positionName = this.positionName(elementData, identSlug, elementIdx);
          let structure = group.structuresByPosition.get(positionName);
          if (!structure) {
            structure = {name: positionName, percent: 0, status: 'success', rules: []};
            group.structuresByPosition.set(positionName, structure);
          }
          structure.rules.push({
            name: ruleName,
            percent: this.statusPercent(type),
            status: type,
            details: [detail],
            shortCode: ruleShortCode,
          });
        }
      });

      group.flatRules.push({
        name: ruleName,
        percent: this.avgStatusPercent(ruleStatuses),
        status: this.worstStatus(ruleStatuses),
        details: ruleDetails,
        shortCode: ruleShortCode,
      });
    });

    const groups: ReportGroup[] = fileOrder.map(key => {
      const group = fileGroups.get(key)!;
      const structures: ReportStructure[] = [...group.structuresByPosition.values()].map(structure => {
        const statuses = structure.rules.map(rule => rule.status);
        return {
          ...structure,
          percent: this.avgStatusPercent(statuses),
          status: this.worstStatus(statuses),
        };
      });
      const groupPercent = this.avgPercent(group.flatRules.map(rule => rule.percent));
      // Wie beim KI-Adapter: eine Datei, deren Prüfregeln durchweg 'unknown' sind, gilt selbst als
      // 'unknown' statt über den (dann bedeutungslosen) Durchschnittsprozentwert. Ohne bewertetes
      // Teilergebnis gibt es auch keinen Prozentwert (groupPercent === null).
      const allRulesUnknown = group.flatRules.length > 0
        && group.flatRules.every(rule => rule.status === 'unknown');
      return {
        name: group.fileName,
        percent: groupPercent,
        status: allRulesUnknown || groupPercent == null ? 'unknown' : statusFromPercent(groupPercent),
        fileFormat: topFileFormat,
        rules: group.flatRules,
        structures,
      };
    });

    // Kein Ergebnis-Eintrag → eine leere Gruppe unter dem (Fallback-)Dateinamen (bisheriges Verhalten).
    if (groups.length === 0) {
      groups.push({
        name: topFileName,
        // Kein Ergebnis-Eintrag heißt "nichts bewertet", nicht "0 % erfüllt".
        percent: null,
        status: 'unknown',
        fileFormat: topFileFormat,
        rules: [],
        structures: [],
      });
    }

    const allFlatRules = groups.flatMap(group => group.rules);
    const overallPercent = this.avgPercent(allFlatRules.map(rule => rule.percent));
    // `raw.score` ist der Backend-Mittelwert über die Regel-Scores und kennt die unbewerteten
    // Ergebnisse nicht. Er darf deshalb nur zählen, wenn überhaupt etwas bewertet wurde – sonst
    // stünde im Bericht wieder eine Prozentzahl über einer Prüfung, die nichts ermittelt hat.
    const score = overallPercent == null
      ? null
      : (raw.score != null ? toPercent(raw.score) : overallPercent);
    const ratedCount = allFlatRules.filter(rule => rule.percent != null).length;
    // Distinkte Regeln zählen (jede Regel läuft je Datei einmal → nicht Dateien×Regeln).
    const distinctRuleCount = new Set(allFlatRules.map(rule => rule.name)).size;

    return {
      meta: {
        checkerName: pickCheckerName(raw),
        checkDate: raw.check_date ? new Date(raw.check_date) : new Date(),
        rulesetName,
        fileName: groups[0]?.name ?? topFileName,
        fileCount: groups.length,
        ruleCount: distinctRuleCount || 1,
        score,
        ratedCount,
        unratedCount: allFlatRules.length - ratedCount,
      },
      groups,
    };
  }

  /**
   * Fachliches Prüfergebnis eines Verdikts.
   *
   * `status` hat Vorrang vor dem booleschen `erfuellt`: bei `teilweise_erfuellt` liefert die
   * Nutzerantwort absichtlich kein `erfuellt`, und ein vorgezogener Boolean-Vergleich hätte den
   * Teilstatus zu „Nicht erfüllt" gemacht. `erfuellt` bleibt der Fallback für Verdikte ohne
   * bekannten Status (z.B. KI-Anweisung mit boolescher Ausgabe).
   *
   * `unknown` ist ein von der KI explizit lieferbarer Wert (siehe Ausgabeformat-Anweisung im
   * Hybrid-Editor) für nicht relevante Dateien oder fehlende Information – bewusst kein
   * stiller Fallback auf `warning`.
   *
   * Trägt der Output überhaupt kein Verdikt, ist das Ergebnis `unknown` und NICHT `warning`:
   * Ein solcher Lauf ist unbewertet (das Backend liefert dafür `unbewertet: true`, siehe
   * `WorkflowEngine#unratedVerdict`) – typischerweise, weil der ausgeführte Pfad nicht in einer
   * Nutzerantwort endet. Das als „Hinweis" (gelb) zu zeigen behauptete ein Prüfergebnis, das nie
   * ermittelt wurde; „unbekannt" (graues Fragezeichen) ist die ehrliche Aussage. Den Unterschied
   * braucht auch die Aggregation: `teilweise_erfuellt` bleibt der einzige echte Warnungs-Fall.
   */
  private verdictStatus(verdict: any): 'success' | 'warning' | 'error' | 'unknown' {
    switch (String(verdict?.status ?? '').toLowerCase()) {
      case 'erfuellt':
      case 'success':
        return 'success';
      case 'nicht_erfuellt':
      case 'failure':
      case 'fail':
      case 'error':
        return 'error';
      case 'teilweise_erfuellt':
      case 'warning':
      case 'warnung':
        return 'warning';
      case 'unknown':
      case 'unbekannt':
        return 'unknown';
    }
    if (verdict?.erfuellt === true) return 'success';
    if (verdict?.erfuellt === false) return 'error';
    // Endet die Regel in einer KI-Anweisung im Prüfmodus statt in einer Nutzerantwort, trägt der
    // Output kein `status`/`erfuellt`, aber das kanonische `pruefergebnis`
    // (SUCCESS/WARNING/FAILURE, siehe ai_instruction_checker). Dessen WARNING ist ein echter
    // Hinweis und darf nicht als „unbekannt" verloren gehen.
    const aiVerdict = parseVerdictValue(verdict?.pruefergebnis);
    if (aiVerdict) return aiVerdict;
    return 'unknown';
  }

  /**
   * Prozentwert eines Einzelergebnisses – `null` für `unknown`.
   *
   * `unknown` trägt kein Ergebnis bei (weder positiv noch negativ) und wurde vorher mit 50 %
   * gewichtet: eine nie geprüfte Regel sah damit aus wie eine halb erfüllte. Wie im KI-Adapter
   * (`answerStatusesToPercent`) fließt sie deshalb gar nicht in den Durchschnitt ein.
   */
  private statusPercent(status: 'success' | 'warning' | 'error' | 'unknown'): number | null {
    if (status === 'success') return 100;
    if (status === 'warning') return 50;
    if (status === 'unknown') return null;
    return 0;
  }

  /**
   * Fallback-Text für die Ergebnis-Karte, wenn keine Nutzerantwort/kein Feld vorliegt.
   *
   * Bei `unknown` ist dieser Text die einzige Stelle, an der der Nutzer erfährt, WARUM kein
   * Ergebnis vorliegt – im Bericht ist ein unbewertetes Ergebnis sonst nicht von einem inhaltlich
   * leeren zu unterscheiden. Die Ursache liefert das Backend im Verdikt mit
   * (`unbewertet_meldung` / `unbewertet_grund` / `unbewertet_schritt`, siehe
   * `WorkflowEngine#unratedVerdict`); fehlt sie, bleibt die allgemeine Erklärung. Der Satz in
   * `unbewertet_meldung` kommt aus dem verzweigenden Modul und benennt dessen Fälle bei ihrem
   * konfigurierten Namen – deshalb hat er Vorrang vor den generischen Formulierungen hier. `warning` bekommt bewusst „Teilweise erfüllt": das ist ein
   * echtes Prüfergebnis und stand vorher irreführend unter demselben Text wie „kein Ergebnis".
   */
  private defaultVerdictText(status: 'success' | 'warning' | 'error' | 'unknown', verdict?: any): string {
    if (status === 'success') return 'Erfüllt';
    if (status === 'error') return 'Nicht erfüllt';
    if (status === 'warning') return 'Teilweise erfüllt';

    // Modulgenaue Meldung: sie nennt die Entscheidung mit den Namen, die der Nutzer selbst
    // konfiguriert hat („Keiner der Fälle „Erdgeschoss", „Dach" ist eingetreten"). Sie entsteht im
    // Modul (shared/branch_info.py) und wird vom Backend um den fehlenden Ausgang ergänzt.
    const message = typeof verdict?.unbewertet_meldung === 'string'
      ? verdict.unbewertet_meldung.trim()
      : '';
    if (message) {
      // Der Zusatz sagt, WAS zu tun ist – und der hängt am Grund: ein fehlender Ausgang wird
      // verbunden, ein verdrahteter Zweig braucht eine Nutzerantwort, und eine leere Prüfebene
      // ist überhaupt kein Verdrahtungsproblem.
      switch (verdict?.unbewertet_grund) {
        case 'zweig_ohne_ergebnis':
          return `Kein Ergebnis: ${message} Dieser Zweig endet ohne Nutzerantwort – ergänze dort `
            + 'eine, damit die Regel ein Ergebnis liefert.';
        case 'verzweigung_ohne_ziel':
          // Die Feststellung („… ist nicht verbunden – der Pfad endet dort") steht schon in der
          // Meldung des Backends (WorkflowEngine#deadEndMessage); hier kommt nur der Hinweis dazu –
          // und der bleibt im Konditional, weil ein nicht verbundener Zweig häufig ABSICHT ist
          // (ein Filter wie „nur Decken prüfen") und keine Fehlkonfiguration.
          return `Kein Ergebnis: ${message} Falls das nicht gewollt ist, verbinde den Ausgang mit `
            + 'einer Nutzerantwort.';
        case 'keine_elemente':
          return `Kein Ergebnis: ${message} Prüfe die Prüfebene und die Eingangsparameter der `
            + 'Regel – ohne Prüfgegenstand kann sie kein Ergebnis liefern.';
        case 'keine_schritte':
          return `Kein Ergebnis: ${message} Ergänze die Prüfschritte und schließe sie mit einer `
            + 'Nutzerantwort ab.';
        default:
          return `Kein Ergebnis: ${message}`;
      }
    }

    const step = typeof verdict?.unbewertet_schritt === 'string' ? verdict.unbewertet_schritt.trim() : '';
    if (verdict?.unbewertet_grund === 'verzweigung_ohne_ziel' && step) {
      return `Kein Ergebnis: Die Verzweigung „${step}“ hat entschieden, aber der zugehörige Zweig `
        + 'ist im Prüflauf nicht verbunden – der Pfad endet dort. Falls das nicht gewollt ist, '
        + 'verbinde den Ausgang mit einer Nutzerantwort.';
    }
    if (verdict?.unbewertet === true) {
      return 'Kein Ergebnis: Der ausgeführte Pfad dieser Regel endet nicht in einer Nutzerantwort. '
        + 'Erst die Nutzerantwort legt fest, ob die Regel als erfüllt, teilweise erfüllt oder nicht '
        + 'erfüllt gilt – bis dahin bleibt das Ergebnis unbewertet.';
    }
    return 'Kein auswertbares Ergebnis: Der letzte Schritt der Regel hat kein Prüfergebnis '
      + '(erfüllt / teilweise erfüllt / nicht erfüllt) geliefert.';
  }

  private avgStatusPercent(statuses: Array<'success' | 'warning' | 'error' | 'unknown'>): number | null {
    return this.avgPercent(statuses.map(status => this.statusPercent(status)));
  }

  /**
   * Mittelwert über die BEWERTETEN Prozentwerte; `null`, wenn keiner bewertet ist.
   *
   * Ein leerer Nenner ergibt keine 0 %: „nicht bewertet" ist nicht „nicht erfüllt".
   */
  private avgPercent(percents: Array<number | null>): number | null {
    const rated = percents.filter((percent): percent is number => percent != null);
    return rated.length > 0
      ? Math.round(rated.reduce((sum, p) => sum + p, 0) / rated.length)
      : null;
  }

  private worstStatus(
    statuses: Array<'success' | 'warning' | 'error' | 'unknown'>,
  ): 'success' | 'warning' | 'error' | 'unknown' {
    const severity = {success: 0, warning: 1, unknown: 2, error: 3} as const;
    return statuses.reduce<'success' | 'warning' | 'error' | 'unknown'>(
      (worst, status) => severity[status] > severity[worst] ? status : worst, 'success');
  }

  /** Benutzerdefinierte Verdikt-Felder (ohne interne/aggregierte Schlüssel) für die Ergebnis-Karte. */
  private verdictFields(verdict: any): Record<string, any> {
    const fields: Record<string, any> = {};
    if (!verdict || typeof verdict !== 'object') return fields;
    for (const [key, value] of Object.entries(verdict)) {
      if (HybridReportAdapter.INTERNAL_VERDICT_KEYS.has(key)) continue;
      if (value === null || value === undefined || value === '') continue;
      // Nur anzeige-würdige, skalare Werte als Ergebnis-Feld; verschachtelte/technische Werte
      // (Logs, Kontext, Arrays, Objekte) werden nicht dargestellt.
      if (typeof value === 'object') continue;
      fields[key] = value;
    }
    return fields;
  }

  /** Positions-/Struktur-Name aus den extrahierten Elementdaten: Identifikator-Slug, sonst erster Wert. */
  private positionName(elementData: any, identSlug: string | undefined, index: number): string {
    if (elementData && typeof elementData === 'object') {
      if (identSlug && elementData[identSlug] != null && String(elementData[identSlug]).trim()) {
        return String(elementData[identSlug]).trim();
      }
      const firstKey = Object.keys(elementData)[0];
      if (firstKey && elementData[firstKey] != null && String(elementData[firstKey]).trim()) {
        return String(elementData[firstKey]).trim();
      }
    }
    return `Position ${index + 1}`;
  }
}

// ─── AI Adapter (prompt-basierte Prüfung, ai_result_aggregator-Format) ───────────

export class AiReportAdapter implements CheckReportAdapter {
  parseReportData(raw: any): NormalizedReport {
    const aggregated: any[] = raw.rules ?? [];
    const rulesetName = raw.projectName || raw.ruleset_name || raw.rulesetName || 'KI Prüfung';
    const groupsByFile = new Map<string, { name: string; fileFormat?: string; rules: ReportRule[] }>();
    // Positions-Modus (Prüfebene ≠ Gesamtdokument): Antworten tragen `element`/`position`.
    const identSlug: string | undefined = raw.identifikator_slug ?? raw.identifikatorSlug ?? undefined;
    const structuresByFile = new Map<string, Map<string, ReportRule[]>>();
    // Gemeinsamer Prüflauf (Prüfmodus COMBINED): keine Ebene je Datei, siehe combinedSourceFiles.
    const combinedFiles = this.combinedSourceFiles(aggregated);

    aggregated.forEach((rule, ruleIndex) => {
      const ruleName = rule.name || `Prüfregel ${ruleIndex + 1}`;
      const allProjectFiles: any[] = Array.isArray(rule.projectFiles) ? rule.projectFiles : [];
      const projectFiles = combinedFiles
        ? this.mergeCombinedProjectFiles(allProjectFiles, combinedFiles.groupName)
        : allProjectFiles;
      // Ersatz-Überschriften je Prüfschritt über ALLE Dateien der Regel (nie = Regelname), falls
      // das Modell für eine Antwort keinen brauchbaren eigenen Titel liefert.
      const canonicalHeadings = this.canonicalStepHeadings(projectFiles, ruleName, identSlug);

      projectFiles.forEach((file, fileIndex) => {
        const fileId = String(file?.id ?? file?.name ?? file?.readable_name ?? `${ruleIndex}-${fileIndex}`);
        const fileName = file?.readable_name || file?.name || file?.id || 'Datei';
        const fileFormat = this.fileFormatOf(file, fileName);
        const answers: any[] = Array.isArray(file?.answers) ? file.answers : [];
        const answerStatuses: Array<'success' | 'warning' | 'error' | 'unknown'> = [];

        const details: ReportDetail[] = [];
        if (answers.length === 0) {
          details.push({
            type: this.mapRuleStatus(file?.status ?? rule?.status),
            text: 'Kein Detailergebnis verfügbar',
            count: 0,
            rawOutput: file,
          });
        } else {
          const stepIndices = this.stepIndicesOf(answers, identSlug);
          answers.forEach((ans, ansIdx) => {
            const fields = this.extractFields(ans);
            // `fields` ist die einzige Quelle: das Ergebnis-Feld (z. B. "Ergebnis": FAILURE)
            // liefert das fachliche Prüfergebnis und damit das Status-Icon. Ist es nicht
            // interpretierbar (z. B. fehlende Information), gilt 'unknown' – ein unklares
            // Ergebnis darf nie grün erscheinen.
            const type = findVerdictInFields(fields) ?? 'unknown';
            answerStatuses.push(type);
            // Überschrift IMMER aus dem Titel dieser Antwort – nur wenn er fehlt oder den
            // Regelnamen echot, greift der kanonische Titel desselben Prüfschritts. Die
            // Schrittfolge ist je Datei unterschiedlich lang (der Prüfprompt lässt die Prüfung bei
            // einem negativen Zwischenschritt abbrechen und zur Handlungsempfehlung springen), ein
            // rein indexbasierter Titel würde einer Datei die Schrittnamen einer anderen
            // aufzwingen (z. B. „Handlungsempfehlung" über den Muss-Kriterien).
            const ownHeading = this.cleanStepHeading(ans?.check_parameter);
            const heading = ownHeading && !this.matchesRuleName(ownHeading, ruleName)
              ? ownHeading
              : canonicalHeadings[stepIndices[ansIdx]];
            const detail: ReportDetail = {
              type,
              heading,
              // Alle Textinhalte kommen aus `fields`; `text` bleibt nur für Adapter
              // mit statischem Ergebnistext (IDS/Hybrid) belegt.
              text: '',
              count: 0,
              fields,
              sourceFiles: this.answerSourceFiles(ans),
              rawOutput: ans,
            };
            details.push(detail);

            // Positions-Modus: Antwort einer Position (Strukturebene) zuordnen.
            const positionName = this.aiPositionName(ans, identSlug);
            if (positionName) {
              if (!structuresByFile.has(fileId)) structuresByFile.set(fileId, new Map());
              const posMap = structuresByFile.get(fileId)!;
              if (!posMap.has(positionName)) posMap.set(positionName, []);
              posMap.get(positionName)!.push({
                name: ruleName,
                percent: this.statusToPercent(type),
                status: type,
                details: [detail],
              });
            }
          });
        }

        const fallbackStatus = this.mapRuleStatus(file?.status ?? rule?.status);
        const percent = this.answerStatusesToPercent(answerStatuses, fallbackStatus);
        // Ergab sich für KEINE Antwort ein interpretierbares Ergebnis (bzw. gibt es gar keine
        // Antworten und auch der Status-Fallback ist unklar), ist auch die Prüfregel insgesamt
        // 'unknown' – ein anhand des (dann bedeutungslosen) Prozentwerts abgeleiteter
        // Erfolg/Warnung/Fehler wäre hier irreführend.
        const allUnknown = answerStatuses.length > 0
          ? answerStatuses.every(status => status === 'unknown')
          : fallbackStatus === 'unknown';
        const ruleEntry: ReportRule = {
          name: ruleName,
          percent,
          status: allUnknown || percent == null
            ? 'unknown'
            : percent >= 100 ? 'success' : percent >= 50 ? 'warning' : 'error',
          details,
          shortCode: this.ruleShortCode(rule),
        };

        const existing = groupsByFile.get(fileId);
        if (existing) {
          existing.rules.push(ruleEntry);
        } else {
          groupsByFile.set(fileId, {name: fileName, fileFormat, rules: [ruleEntry]});
        }
      });
    });

    const groups: ReportGroup[] = Array.from(groupsByFile.entries()).map(([fileId, group]) => {
      const totalRules = group.rules.length || 1;
      const avgPercent = averageRatedPercent(group.rules.map(rule => rule.percent));
      // Eine Datei, deren Prüfregeln durchweg 'unknown' sind (nicht relevant/keine Information),
      // gilt selbst als 'unknown' statt über den (dann bedeutungslosen) Durchschnittsprozentwert.
      const allRulesUnknown = group.rules.length > 0 && group.rules.every(rule => rule.status === 'unknown');
      const posMap = structuresByFile.get(fileId);
      const structures: ReportStructure[] | undefined = posMap
        ? [...posMap.entries()].map(([name, rules]) => {
          const statuses = rules.map(r => r.status);
          return {
            name,
            percent: averageRatedPercent(rules.map(r => r.percent)),
            status: worstStatus(statuses),
            rules,
          };
        })
        : undefined;
      return {
        name: group.name,
        percent: avgPercent,
        status: allRulesUnknown || avgPercent == null
          ? 'unknown'
          : avgPercent >= 100 ? 'success' : avgPercent >= 50 ? 'warning' : 'error',
        fileFormat: group.fileFormat,
        rules: group.rules,
        structures,
      };
    });

    const effectiveGroups = groups.length > 0
      ? groups
      : [{
        name: rulesetName,
        percent: null,
        status: 'unknown' as const,
        rules: [],
      }];

    const avgPercent = averageRatedPercent(effectiveGroups.map(group => group.percent));

    const score = this.normalizeScoreToPercent(raw.score);
    const allRules = effectiveGroups.flatMap(group => group.rules);
    const ratedCount = allRules.filter(rule => rule.percent != null).length;

    return {
      meta: {
        checkerName: pickCheckerName(raw),
        checkDate: raw.checkDate ? new Date(raw.checkDate) : new Date(),
        rulesetName,
        fileName: effectiveGroups[0]?.name ?? rulesetName,
        // Im gemeinsamen Prüflauf gibt es nur EINE Gruppe, aber sehr wohl mehrere geprüfte Dateien:
        // die Anzahl kommt dann aus den herangezogenen Quelldateien, sonst stünde im Kopf und im
        // PDF-Export „1 geprüfte Datei" über einem Lauf über zwölf.
        fileCount: combinedFiles ? combinedFiles.count : effectiveGroups.length,
        ruleCount: aggregated.length,
        // Ohne eine einzige bewertete Regel gibt es kein Gesamtergebnis – auch dann nicht, wenn
        // das Backend einen Score mitgeschickt hat (er kennt die unbewerteten Antworten nicht).
        score: avgPercent == null ? null : (score ?? avgPercent),
        ratedCount,
        unratedCount: allRules.length - ratedCount,
      },
      groups: effectiveGroups,
    };
  }

  /**
   * Die herangezogenen Quelldateien EINER Antwort – die Grundlage für die Quellen-Links im Bericht.
   *
   * Nur im gemeinsamen Prüflauf (Prüfmodus COMBINED) tragen Antworten `source_files`; im
   * Einzeldatei-Modus ist die geprüfte Datei die Berichtsgruppe selbst und braucht keinen Link im
   * Text. Einträge ohne `id` fallen heraus: der Aggregator legt für nicht zuordenbare Antworten
   * Platzhalter mit leerer `id` an („Ohne Dateibezug"/„Keine passende Datei") – daraus würde ein
   * Link ins Leere. Ebenso Einträge ohne Namen: ohne den im Text genannten Namen gibt es nichts zu
   * verlinken.
   */
  private answerSourceFiles(answer: any): ReportSourceFile[] | undefined {
    const sources = Array.isArray(answer?.source_files) ? answer.source_files : [];
    const resolved: ReportSourceFile[] = [];
    const seen = new Set<string>();
    for (const source of sources) {
      if (!source || typeof source !== 'object') continue;
      const id = String(source.id ?? '').trim();
      const name = String(source.name ?? '').trim();
      if (!id || !name || seen.has(id)) continue;
      seen.add(id);
      resolved.push({id, name});
    }
    return resolved.length > 0 ? resolved : undefined;
  }

  /**
   * Erkennt einen gemeinsamen Prüflauf (Prüfmodus COMBINED) und liefert die herangezogenen Dateien.
   *
   * Kennzeichen sind die `source_files` an den Antworten: sie entstehen nur im gemeinsamen Lauf.
   * Bewusst datengetrieben und ohne eigenes Flag – so gilt die Erkennung auch für bereits
   * gespeicherte Berichte, ohne dass das Prüfmodul etwas Neues mitschicken muss (dieselbe
   * Denkweise wie `_is_combined_result` im ai_result_aggregator).
   *
   * Im gemeinsamen Lauf gibt es kein Ergebnis JE DATEI: eine Bewertung gilt für die gesamte
   * Dateimenge. Der Aggregator sortiert sie trotzdem unter jede ihrer Quelldateien ein, damit im
   * dateizentrierten Bericht nichts verschwindet. Der Bericht führt sie deshalb wieder zu EINER
   * Gruppe zusammen – die Dateiebene entfällt damit überall von selbst (`resolveRenderedLevelKeys`
   * entfernt sie bei ≤ 1 Gruppe), und keine Bewertung erscheint mehrfach.
   */
  private combinedSourceFiles(aggregated: any[]): { count: number; groupName: string } | null {
    const names = new Map<string, string>();
    let combined = false;
    for (const rule of aggregated ?? []) {
      for (const file of Array.isArray(rule?.projectFiles) ? rule.projectFiles : []) {
        for (const answer of Array.isArray(file?.answers) ? file.answers : []) {
          const sources = Array.isArray(answer?.source_files) ? answer.source_files : [];
          for (const source of sources) {
            if (!source || typeof source !== 'object') continue;
            combined = true;
            const id = String(source.id ?? source.name ?? '');
            if (id) names.set(id, String(source.name ?? source.id ?? ''));
          }
        }
      }
    }
    if (!combined) return null;
    const distinct = [...names.values()].filter(name => name.length > 0);
    return {
      count: Math.max(names.size, 1),
      // Bei genau einer herangezogenen Datei bleibt ihr Name erhalten – er steht dann im Kopf des
      // Berichts, wo sonst die Anzahl der geprüften Dateien steht.
      groupName: distinct.length === 1 ? distinct[0] : 'Alle Dateien',
    };
  }

  /**
   * Führt die je Quelldatei aufgeteilten Einträge einer Prüfregel zu einem einzigen zusammen.
   *
   * Eine Antwort, die sich auf mehrere Dateien stützt, steht im Aggregat unter jeder von ihnen –
   * dieselbe Bewertung mehrfach. Die Deduplizierung über die serialisierte Antwort ist exakt:
   * verglichen werden Kopien derselben Antwort, keine bloß ähnlichen.
   */
  private mergeCombinedProjectFiles(projectFiles: any[], groupName: string): any[] {
    const answers: any[] = [];
    const seen = new Set<string>();
    for (const file of projectFiles) {
      for (const answer of Array.isArray(file?.answers) ? file.answers : []) {
        const key = JSON.stringify(answer);
        if (seen.has(key)) continue;
        seen.add(key);
        answers.push(answer);
      }
    }
    // Der Status ist im gemeinsamen Lauf für alle Einträge derselbe (ein Ergebnis je Regel); er
    // trägt nur den Fall „keine einzige Antwort" (übersprungen/Fehler).
    return [{...(projectFiles[0] ?? {}), id: '__combined__', name: groupName, answers}];
  }

  private answerStatusesToPercent(
    statuses: Array<'success' | 'warning' | 'error' | 'unknown'>,
    fallbackStatus: 'success' | 'warning' | 'error' | 'unknown',
  ): number | null {
    // 'unknown'-Antworten tragen kein Ergebnis bei (weder positiv noch negativ) und fließen
    // deshalb nicht in den Durchschnitt ein; bleiben nur solche übrig, entscheidet der Fallback –
    // und ist auch der 'unknown', gibt es keinen Prozentwert (null), keine 50 %.
    const known = statuses.filter(status => status !== 'unknown');
    if (known.length === 0) {
      return this.statusToPercent(fallbackStatus);
    }

    const weightedSum = known.reduce((sum, status) => {
      if (status === 'success') return sum + 1;
      if (status === 'warning') return sum + 0.5;
      return sum;
    }, 0);

    return Math.round((weightedSum / known.length) * 100);
  }

  /** Positions-/Struktur-Name einer KI-Antwort (Identifikator-Slug, sonst erster element-Wert, sonst `position`). */
  private aiPositionName(ans: any, identSlug: string | undefined): string {
    const element = ans?.element;
    if (element && typeof element === 'object') {
      if (identSlug && element[identSlug] != null && String(element[identSlug]).trim()) {
        return String(element[identSlug]).trim();
      }
      const firstKey = Object.keys(element)[0];
      if (firstKey && element[firstKey] != null && String(element[firstKey]).trim()) {
        return String(element[firstKey]).trim();
      }
    }
    return typeof ans?.position === 'string' ? ans.position.trim() : '';
  }

  /** Dateiformat für die Spalte „Dateiformat": bevorzugt `fileType`, sonst Endung aus dem Namen. */
  private fileFormatOf(file: any, fileName: string): string | undefined {
    const type = (file?.fileType ?? file?.file_type ?? file?.type ?? '').toString().trim();
    if (type) return type.toUpperCase();
    const match = /\.([a-z0-9]+)\s*$/i.exec(fileName ?? '');
    return match ? match[1].toUpperCase() : undefined;
  }

  /** Prüfregelkürzel für die Spalte „Prüfregelkürzel" (falls in den Rohdaten vorhanden). */
  private ruleShortCode(rule: any): string | undefined {
    const code = (rule?.ruleShortCode ?? rule?.shortCode ?? rule?.short_code ?? rule?.rule_short_code ?? '')
      .toString().trim();
    return code || undefined;
  }

  private normalizeScoreToPercent(score: unknown): number | null {
    if (typeof score !== 'number' || !Number.isFinite(score)) {
      return null;
    }
    if (score <= 1) {
      return Math.round(score * 100);
    }
    return Math.round(Math.max(0, Math.min(score, 100)));
  }

  private mapRuleStatus(status: string | undefined): 'success' | 'warning' | 'error' | 'unknown' {
    switch ((status ?? '').toLowerCase()) {
      case 'success':
        return 'success';
      // Übersprungen, weil die Datei für die Regel nicht relevant war – kein Ergebnis, nicht
      // automatisch bestanden.
      case 'skipped_not_applicable':
        return 'unknown';
      case 'warning':
        return 'warning';
      case 'error':
      case 'fail':
      case 'failed':
        return 'error';
      // Kein bekannter Status geliefert: unklares statt stillschweigend grünes/gelbes Ergebnis.
      default:
        return 'unknown';
    }
  }

  private statusToPercent(status: 'success' | 'warning' | 'error' | 'unknown'): number | null {
    if (status === 'success') return 100;
    if (status === 'warning') return 50;
    // 'unknown' hat kein Ergebnis – vorher 50 %, was eine halb erfüllte Prüfung behauptete.
    if (status === 'unknown') return null;
    return 0;
  }

  /**
   * Übernimmt die vollständig dynamischen KI-Ausgabefelder unverändert.
   * Die KI liefert je Antwort ein `fields`-Objekt, dessen Schlüssel die vom Nutzer
   * konfigurierten Ausgabeparameter-Namen sind. Es werden keine festen Felder erzwungen.
   */
  private extractFields(ans: any): Record<string, any> | undefined {
    const fields = ans?.fields;
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      return fields as Record<string, any>;
    }
    return undefined;
  }

  /** Vergleicht einen Text (case-insensitiv, getrimmt) mit dem Regelnamen. */
  private matchesRuleName(text: string | undefined, ruleName: string): boolean {
    if (!text) return false;
    return text.trim().toLowerCase() === (ruleName ?? '').trim().toLowerCase();
  }

  /**
   * Prüfschritt-Index jeder Antwort INNERHALB ihrer Position.
   *
   * Im Positions-Modus (Prüfebene ≠ Gesamtdokument) liegen alle Positionen einer Datei in einer
   * flachen `answers`-Liste: Position 1 Schritt 1..n, Position 2 Schritt 1..n, … Der Listenindex
   * ist damit KEIN Schrittindex – er zählt über alle Positionen hoch. Ohne Positionen (leerer
   * Positionsname für jede Antwort) ist das Ergebnis wieder der laufende Index.
   */
  private stepIndicesOf(answers: any[], identSlug: string | undefined): number[] {
    const stepsPerPosition = new Map<string, number>();
    return answers.map(ans => {
      const position = this.aiPositionName(ans, identSlug);
      const step = stepsPerPosition.get(position) ?? 0;
      stepsPerPosition.set(position, step + 1);
      return step;
    });
  }

  /**
   * Ersatz-Überschriften je Prüfschritt für Antworten OHNE brauchbaren eigenen Titel.
   * Das Modell lässt `ans.check_parameter` gelegentlich leer oder echot den Regelnamen
   * (Prüfparameter == Regelname) – beides wäre als Schritt-Überschrift falsch. Pro Schritt wird
   * daher der erste sinnvolle Titel (nicht leer, nicht = Regelname) aus allen Dateien und
   * Positionen vorgehalten, sonst der neutrale Fallback „Prüfschritt {n}". Der Regelname wird nie
   * als Überschrift verwendet. Nur Rückfallebene: der eigene Titel einer Antwort hat immer
   * Vorrang, weil die Schrittfolge je Datei abweichen kann (Abbruch mit Sprung zur
   * Handlungsempfehlung).
   *
   * Der Schritt-Index kommt aus {@link stepIndicesOf}, nicht aus der Listenposition: im
   * Positions-Modus lieferte der Listenindex sonst „Prüfschritt 5" für den ERSTEN Schritt der
   * fünften Position – und zwar auch dann, wenn andere Positionen denselben Schritt sauber
   * benannt haben.
   */
  private canonicalStepHeadings(
    projectFiles: any[], ruleName: string, identSlug?: string,
  ): Array<string | undefined> {
    const headings: Array<string | undefined> = [];
    let stepCount = 0;
    for (const file of projectFiles ?? []) {
      const answers: any[] = Array.isArray(file?.answers) ? file.answers : [];
      const stepIndices = this.stepIndicesOf(answers, identSlug);
      answers.forEach((ans, ansIdx) => {
        const step = stepIndices[ansIdx];
        stepCount = Math.max(stepCount, step + 1);
        if (headings[step]) return;
        const candidate = this.cleanStepHeading(ans?.check_parameter);
        if (candidate && !this.matchesRuleName(candidate, ruleName)) headings[step] = candidate;
      });
    }
    // Einschrittige Regel ohne brauchbaren Titel: gar keine Überschrift. „Prüfschritt 1" trägt
    // keine Information – die Regel steht bereits als Zeile daneben, und eine Ordnungszahl ohne
    // zweiten Schritt ordnet nichts. Ab zwei Schritten bleibt die Nummer als Reihenfolge sinnvoll.
    if (stepCount < 2) return headings;
    for (let i = 0; i < stepCount; i++) {
      if (!headings[i]) headings[i] = `Prüfschritt ${i + 1}`;
    }
    return headings;
  }

  /**
   * Bereinigt den vom Modell gelieferten Prüfschritt-Titel für die Anzeige als Überschrift:
   * schneidet ein evtl. vorhandenes "Schritt N:" / "Step N:" / "N." / "N)"-Präfix ab
   * (Abwärtskompatibilität mit älteren Ergebnissen, die noch nummeriert waren).
   */
  private cleanStepHeading(raw: unknown): string | undefined {
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) return undefined;
    const cleaned = text
      .replace(/^\s*(schritt|step)\s*\d+\s*[:.)-]\s*/i, '')
      .replace(/^\s*\d+\s*[:.)-]\s*/, '')
      .trim();
    // Bleibt nach dem Abschneiden nichts übrig, war der Titel reine Nummerierung („7.").
    const candidate = cleaned || text;
    return AiReportAdapter.GENERIC_STEP_HEADING.test(candidate) ? undefined : candidate;
  }

  /**
   * Reine Nummerierung ohne Inhalt („Prüfschritt 5", „Schritt 2", „Punkt 3", „5.") – als
   * Überschrift wertlos und irreführend, weil die Nummer aus Sicht des Modells zählt und nicht
   * aus Sicht des Berichts. Solche Titel werden verworfen, damit der kanonische Titel desselben
   * Prüfschritts (aus einer anderen Position/Datei) greift; erst wenn auch der fehlt, entsteht
   * der neutrale Platzhalter mit der korrekten Schrittnummer.
   */
  private static readonly GENERIC_STEP_HEADING =
    /^(prüf|pruef)?(schritt|step|punkt|teil|abschnitt)?\s*\d+\s*[.)]?$/i;
}

// ─── Factory ────────────────────────────────────────────────────────────────────

export function getReportAdapter(checkRuleType: string): CheckReportAdapter {
  switch (checkRuleType?.toUpperCase()) {
    case 'IDS':
      return new IdsReportAdapter();
    case 'HYBRID':
      return new HybridReportAdapter();
    // Prompt-basierte Prüfung – 'AI' ist kanonisch, 'HOAI'/'KI' bleiben aus
    // Abwärtskompatibilität (alte Definitionen/CheckResults) erhalten.
    case 'AI':
    case 'HOAI':
    case 'KI':
      return new AiReportAdapter();
    default:
      // Fallback auf Hybrid-Adapter, da dieser am generischsten ist
      return new HybridReportAdapter();
  }
}
